import React, { useState, useEffect } from "react";
import { ArrowDownCircle, ArrowUpCircle, Sparkles } from "lucide-react";
import { enhanceGoalDescription } from "../../api/api";
import { useLanguage } from "../../i18n/LanguageContext";

const GoalForm = ({ onClose, onSave, editGoal = null }) => {
  const { t } = useLanguage();
  const [goal, setGoal] = useState({
    title: "",
    type: "One-time",
    targetDate: "",
    category: "",
    priority: "Low",
    description: "",
    progress: 0,
  });

  const [enhancingDescription, setEnhancingDescription] = useState(false);
  const [enhanceError, setEnhanceError] = useState(null);
  const [enhancedPreview, setEnhancedPreview] = useState("");
  const [formError, setFormError] = useState("");

  const priorityLevels = ["Low", "Medium", "High"];
  const priorityLabels = {
    Low: t("goal.priorityLow"),
    Medium: t("goal.priorityMedium"),
    High: t("goal.priorityHigh"),
  };
  const getPriorityString = (sliderValue) => priorityLevels[sliderValue];
  const getPrioritySliderValue = (priorityString) =>
    priorityLevels.indexOf(priorityString);

  useEffect(() => {
    if (editGoal) {
      setGoal({
        ...editGoal,
        targetDate: editGoal.targetDate
          ? editGoal.targetDate.split("T")[0]
          : "",
        priority: editGoal.priority || "Low",
      });
    } else {
      setGoal({
        title: "",
        type: "One-time",
        targetDate: "",
        category: "",
        priority: "Low",
        description: "",
        progress: 0,
      });
    }
    setFormError("");
    setEnhanceError(null);
    setEnhancedPreview("");
  }, [editGoal]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    if (formError) {
      setFormError("");
    }

    if (name === "priority") {
      const sliderValue = parseInt(value, 10);
      setGoal((prev) => ({
        ...prev,
        priority: getPriorityString(sliderValue),
      }));
    } else if (type === "range") {
      const newValue = parseInt(value, 10);
      setGoal((prev) => ({
        ...prev,
        [name]: newValue,
      }));
    } else if (name === "type") {
      setGoal((prev) => ({
        ...prev,
        type: value,
        targetDate: value === "Recurring" ? "" : prev.targetDate,
      }));
    } else {
      setGoal((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!goal.title || !goal.category) {
      setFormError(t("goal.required"));
      return;
    }
    if (goal.type === "One-time" && !goal.targetDate) {
      setFormError(t("goal.targetRequired"));
      return;
    }
    if (goal.targetDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const targetDate = new Date(goal.targetDate);
      targetDate.setHours(0, 0, 0, 0);
      if (targetDate < today) {
        setFormError(t("goal.targetPast"));
        return;
      }
    }

    const goalDataToSave = { ...goal };
    if (goalDataToSave.type === "Recurring") {
      delete goalDataToSave.targetDate;
    }

    onSave(goalDataToSave);
  };

  const handleEnhanceDescription = async () => {
    setEnhancingDescription(true);
    setEnhanceError(null);
    try {
      const enhancedText = await enhanceGoalDescription(
        goal.title,
        goal.description
      );
      setEnhancedPreview(enhancedText);
    } catch (error) {
      setEnhanceError(t("goal.enhanceFailed"));
      console.error(error);
    } finally {
      setEnhancingDescription(false);
    }
  };

  const handleAcceptPreview = () => {
    setGoal((prev) => ({ ...prev, description: enhancedPreview }));
    setEnhancedPreview("");
  };

  const handleDiscardPreview = () => {
    setEnhancedPreview("");
  };

  const renderPrioritySlider = () => (
    <div>
      <label
        htmlFor="priority"
        className="block text-sm font-medium text-gray-700"
      >
        {t("goal.priority")}: {priorityLabels[goal.priority] || goal.priority}
      </label>
      <div className="flex items-center space-x-2 mt-1">
        <ArrowDownCircle className="h-5 w-5 text-gray-500" />
        <input
          type="range"
          id="priority"
          name="priority"
          min="0"
          max="2"
          step="1"
          value={getPrioritySliderValue(goal.priority)}
          onChange={handleChange}
          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer range-lg"
        />
        <ArrowUpCircle className="h-5 w-5 text-red-500" />
      </div>
      <div className="flex justify-between text-xs text-gray-500 mt-1">
        <span>{t("goal.priorityLow")}</span>
        <span>{t("goal.priorityMedium")}</span>
        <span>{t("goal.priorityHigh")}</span>
      </div>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4">
      {formError && (
        <p className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
          {formError}
        </p>
      )}

      <div>
        <label
          htmlFor="title"
          className="block text-sm font-medium text-gray-700"
        >
          {t("goal.title")}
        </label>
        <input
          type="text"
          id="title"
          name="title"
          value={goal.title}
          onChange={handleChange}
          placeholder={t("goal.titlePlaceholder")}
          className="mt-1 block w-full rounded-xl border-0 bg-gray-50 px-4 py-2.5 text-gray-900 ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:ring-purple-500 transition-all duration-150"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          {t("goal.type")}
        </label>
        <div className="mt-1 flex space-x-4">
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="type"
              value="One-time"
              checked={goal.type === "One-time"}
              onChange={handleChange}
              className="form-radio text-purple-600"
            />
            <span className="ml-2 text-gray-700">{t("goal.oneTime")}</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="type"
              value="Recurring"
              checked={goal.type === "Recurring"}
              onChange={handleChange}
              className="form-radio text-purple-600"
            />
            <span className="ml-2 text-gray-700">{t("goal.recurring")}</span>
          </label>
        </div>
      </div>

      <div>
        <label
          htmlFor="category"
          className="block text-sm font-medium text-gray-700"
        >
          {t("goal.category")}
        </label>
        <input
          type="text"
          id="category"
          name="category"
          value={goal.category}
          onChange={handleChange}
          placeholder={t("goal.categoryPlaceholder")}
          className="mt-1 block w-full rounded-xl border-0 bg-gray-50 px-4 py-2.5 text-gray-900 ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:ring-purple-500 transition-all duration-150"
          required
        />
      </div>

      {renderPrioritySlider()}

      <div>
        <label
          htmlFor="targetDate"
          className="block text-sm font-medium text-gray-700"
        >
          {t("goal.targetDate")}
        </label>
        <input
          type="date"
          id="targetDate"
          name="targetDate"
          value={goal.targetDate}
          onChange={handleChange}
          disabled={goal.type === "Recurring"}
          className={`mt-1 block w-full rounded-xl border-0 bg-gray-50 px-4 py-2.5 text-gray-900 ring-1 ring-inset ring-gray-200 focus:bg-white focus:ring-2 focus:ring-purple-500 transition-all duration-150 ${
            goal.type === "Recurring" ? "bg-gray-100 cursor-not-allowed" : ""
          }`}
        />
      </div>

      <div>
        <label
          htmlFor="description"
          className="block text-sm font-medium text-gray-700"
        >
          {t("goal.descriptionOptional")}
        </label>
        <textarea
          id="description"
          name="description"
          value={goal.description}
          onChange={handleChange}
          rows="3"
          placeholder={t("goal.descriptionPlaceholder")}
          className="mt-1 block w-full rounded-xl border-0 bg-gray-50 px-4 py-2.5 text-gray-900 ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:ring-purple-500 transition-all duration-150"
        ></textarea>
        <button
          type="button"
          onClick={handleEnhanceDescription}
          disabled={enhancingDescription || !goal.title}
          className="mt-2 px-3 py-1 text-sm font-medium rounded-md flex items-center space-x-1 bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400"
        >
          <Sparkles className="h-4 w-4" />
          {enhancingDescription ? t("goal.enhancing") : t("goal.enhance")}
        </button>
        {enhanceError && (
          <p className="mt-2 text-sm text-red-600">{enhanceError}</p>
        )}
        {enhancedPreview && (
          <div className="mt-3 rounded-xl border border-purple-100 bg-purple-50 p-4">
            <p className="text-sm leading-6 text-gray-700">{enhancedPreview}</p>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={handleAcceptPreview}
                className="rounded-full bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-700"
              >
                {t("goal.acceptPreview")}
              </button>
              <button
                type="button"
                onClick={handleDiscardPreview}
                className="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                {t("goal.discardPreview")}
              </button>
            </div>
          </div>
        )}
      </div>

      {editGoal?.id && (
        <div>
          <label
            htmlFor="progress"
            className="block text-sm font-medium text-gray-700"
          >
            {t("goal.progress")} (%)
          </label>
          <input
            type="range"
            id="progress"
            name="progress"
            min="0"
            max="100"
            value={goal.progress}
            onChange={handleChange}
            className="mt-1 block w-full"
          />
          <p className="text-sm text-gray-600 mt-1">
            {goal.progress}% {t("goal.complete")}
          </p>
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          {t("common.cancel")}
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
        >
          {editGoal ? t("goals.edit") : t("goals.create")}
        </button>
      </div>
    </form>
  );
};

export default GoalForm;
